import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface LeaderboardItem {
  auditor_id: number;
  username: string;
  total_points: number;
  audits_completed: number;
  low_risk_audits: number;
  evidences_fixed: number;
}

interface ActivityItem {
  id: number;
  points: number;
  reason: string;
  schedule_id: number | null;
  evidence_id: number | null;
  created_at: number;
}

const GamificationPage: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'all' | '7d' | '30d'>('all');
  const [minPoints, setMinPoints] = useState<number>(0);
  const [selectedAuditor, setSelectedAuditor] = useState<LeaderboardItem | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<{ data: { items: LeaderboardItem[] } }>(
          '/gamification/leaderboard',
          {
            params: { period },
          },
        );
        setLeaderboard(res.data.data.items || []);
      } catch (e) {
        setError('No se pudo cargar el ranking de gamificación');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [period]);

  const filtered = leaderboard.filter(item => item.total_points >= minPoints);

  const loadActivity = async (auditor: LeaderboardItem) => {
    setSelectedAuditor(auditor);
    setActivity([]);
    setActivityError(null);
    setActivityLoading(true);
    try {
      const res = await apiClient.get<{
        data: { auditor: { id: number; username: string }; items: ActivityItem[] };
      }>(`/gamification/auditors/${auditor.auditor_id}/activity`, {
        params: { period },
      });
      setActivity(res.data.data.items || []);
    } catch (e) {
      setActivityError('No se pudo cargar la actividad del auditor');
    } finally {
      setActivityLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Gamificación
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Ranking de auditores y actividad reciente basada en puntos de juego.
        </p>
      </header>

      {/* Filtros superiores */}
      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 18,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(148, 163, 184, 0.35)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Periodo</span>
            <select
              value={period}
              onChange={e => setPeriod(e.target.value as 'all' | '7d' | '30d')}
              style={{
                minWidth: 140,
                padding: '6px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                color: '#e5e7eb',
                fontSize: 12,
                outline: 'none',
              }}
            >
              <option value="all">Todos</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Puntos mínimos</span>
            <input
              type="number"
              value={minPoints}
              onChange={e => setMinPoints(Number(e.target.value) || 0)}
              style={{
                width: 100,
                padding: '6px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                color: '#e5e7eb',
                fontSize: 12,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </section>

      {loading && <p style={{ fontSize: 12 }}>Cargando ranking...</p>}
      {error && <p style={{ color: '#fecaca', fontSize: 12 }}>{error}</p>}

      {!loading && !error && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1.4fr)',
            gap: 16,
            alignItems: 'flex-start',
          }}
        >
          {/* Ranking */}
          <section
            style={{
              padding: 16,
              borderRadius: 18,
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#e5e7eb',
                }}
              >
                Ranking de auditores
              </h2>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>Total: {filtered.length}</span>
            </div>

            {filtered.length === 0 ? (
              <p style={{ fontSize: 12 }}>No hay auditores que cumplan el filtro actual.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                      }}
                    >
                      <th style={{ padding: '6px 4px' }}>#</th>
                      <th style={{ padding: '6px 4px' }}>Auditor</th>
                      <th style={{ padding: '6px 4px' }}>Puntos</th>
                      <th style={{ padding: '6px 4px' }}>Audits comp.</th>
                      <th style={{ padding: '6px 4px' }}>Bajo riesgo</th>
                      <th style={{ padding: '6px 4px' }}>Evid. corregidas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row, index) => (
                      <tr
                        key={row.auditor_id}
                        onClick={() => loadActivity(row)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor:
                            selectedAuditor?.auditor_id === row.auditor_id
                              ? 'rgba(56, 189, 248, 0.12)'
                              : 'transparent',
                          borderBottom: '1px solid rgba(31, 41, 55, 0.9)',
                        }}
                      >
                        <td style={{ padding: '6px 4px' }}>{index + 1}</td>
                        <td style={{ padding: '6px 4px' }}>{row.username}</td>
                        <td style={{ padding: '6px 4px' }}>{row.total_points}</td>
                        <td style={{ padding: '6px 4px' }}>{row.audits_completed}</td>
                        <td style={{ padding: '6px 4px' }}>{row.low_risk_audits}</td>
                        <td style={{ padding: '6px 4px' }}>{row.evidences_fixed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Actividad reciente */}
          <section
            style={{
              padding: 16,
              borderRadius: 18,
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              minHeight: 180,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {selectedAuditor
                ? `Actividad reciente de ${selectedAuditor.username}`
                : 'Actividad reciente'}
            </h2>

            {!selectedAuditor && (
              <p style={{ fontSize: 12, marginTop: 8 }}>
                Selecciona un auditor del ranking para ver su actividad.
              </p>
            )}

            {selectedAuditor && (
              <div style={{ marginTop: 8 }}>
                {activityLoading && <p style={{ fontSize: 12 }}>Cargando actividad...</p>}
                {activityError && (
                  <p style={{ color: '#fecaca', fontSize: 12 }}>{activityError}</p>
                )}
                {!activityLoading && !activityError && activity.length === 0 && (
                  <p style={{ fontSize: 12 }}>
                    No hay actividad en el periodo seleccionado.
                  </p>
                )}
                {!activityLoading && !activityError && activity.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            textAlign: 'left',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                          }}
                        >
                          <th style={{ padding: '6px 4px' }}>Fecha</th>
                          <th style={{ padding: '6px 4px' }}>Puntos</th>
                          <th style={{ padding: '6px 4px' }}>Razón</th>
                          <th style={{ padding: '6px 4px' }}>Schedule</th>
                          <th style={{ padding: '6px 4px' }}>Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activity.map(item => (
                          <tr
                            key={item.id}
                            style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                          >
                            <td style={{ padding: '6px 4px' }}>
                              {new Date(item.created_at * 1000).toLocaleString()}
                            </td>
                            <td style={{ padding: '6px 4px' }}>{item.points}</td>
                            <td style={{ padding: '6px 4px' }}>{item.reason}</td>
                            <td style={{ padding: '6px 4px' }}>{item.schedule_id ?? '-'}</td>
                            <td style={{ padding: '6px 4px' }}>{item.evidence_id ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default GamificationPage;
